import * as React from 'react';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ExternalLink, Phone, Video, MessageSquare, Filter, Building2, Mail, Send, Link2, Check } from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { env } from '@/lib/env';

/**
 * Operator sales kanban — P1.9.
 *
 * Five-column kanban for the founder cold-call sprint + directory
 * pre-gen funnel. Stage transitions persist via PATCH
 * /api/operator/sales/prospects/:id. Filter by source (directory /
 * cold-call / fb-group / referral).
 */
export const Route = createFileRoute('/_app/operator/sales')({
  staticData: { crumb: 'Sales' },
  beforeLoad: ({ context }) => {
    const ctx = context as { session: { role: string } };
    if (ctx.session.role !== 'operator') throw redirect({ to: '/' });
  },
  component: OperatorSales,
});

type Stage = 'not_contacted' | 'called' | 'demoed' | 'signed' | 'churned' | 'dropped';

interface Prospect {
  id: string;
  tenant_id: string | null;
  source: string;
  source_id: string | null;
  clinic_name: string;
  city: string | null;
  vertical: string;
  contact_email: string | null;
  contact_phone: string | null;
  directory_url: string | null;
  preview_url: string | null;
  stage: Stage;
  cohort: string | null;
  loom_url: string | null;
  notes: string | null;
  last_contact_at: number | null;
  next_contact_at: number | null;
  updated_at: number;
}

const COLUMNS: Array<{ key: Stage; label: string; tint: string }> = [
  { key: 'not_contacted', label: 'Nie skontaktowani', tint: 'bg-surface' },
  { key: 'called',        label: 'Po telefonie',      tint: 'bg-amber-50' },
  { key: 'demoed',        label: 'Po demo',           tint: 'bg-blue-50' },
  { key: 'signed',        label: 'Podpisani',         tint: 'bg-green-50' },
  { key: 'churned',       label: 'Odeszli',           tint: 'bg-red-50' },
];

function OperatorSales() {
  const qc = useQueryClient();
  const [sourceFilter, setSourceFilter] = React.useState<string>('');

  const prospects = useQuery({
    queryKey: ['operator', 'sales', sourceFilter],
    queryFn: async (): Promise<Prospect[]> => {
      const q = sourceFilter ? `?source=${sourceFilter}` : '';
      const res = await fetch(`${env.apiBaseUrl}/api/operator/sales/prospects${q}`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error(`prospects_${res.status}`);
      const json = (await res.json()) as { prospects: Prospect[] };
      return json.prospects;
    },
    refetchInterval: 30_000,
  });

  const update = useMutation({
    mutationFn: async (input: { id: string; patch: Partial<Prospect> }) => {
      const res = await fetch(
        `${env.apiBaseUrl}/api/operator/sales/prospects/${input.id}`,
        {
          method: 'PATCH',
          credentials: 'include',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify(input.patch),
        },
      );
      if (!res.ok) throw new Error(`update_${res.status}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['operator', 'sales'] }),
  });

  const byStage = React.useMemo(() => {
    const buckets = new Map<Stage, Prospect[]>();
    for (const col of COLUMNS) buckets.set(col.key, []);
    for (const p of prospects.data ?? []) {
      if (p.stage === 'dropped') continue;
      const bucket = buckets.get(p.stage) ?? [];
      bucket.push(p);
      buckets.set(p.stage, bucket);
    }
    return buckets;
  }, [prospects.data]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 font-display text-2xl font-semibold tracking-tight text-text">
            <Building2 className="size-5 text-accent" aria-hidden="true" />
            Sales pipeline
          </h1>
          <p className="mt-1 text-sm text-text-muted">
            Cold-call top-200 + directory pre-gen claim funnel. Cel: 20 płatnych klinik.
          </p>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <Filter className="size-3.5 text-text-subtle" aria-hidden="true" />
          {['', 'directory', 'cold-call', 'fb-group', 'referral'].map((s) => (
            <button
              key={s || 'all'}
              type="button"
              onClick={() => setSourceFilter(s)}
              className={
                sourceFilter === s
                  ? 'rounded-full bg-text px-3 py-1 font-medium text-bg'
                  : 'rounded-full bg-surface px-3 py-1 text-text-muted hover:text-text'
              }
            >
              {s || 'wszystkie'}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-5">
        {COLUMNS.map((col) => {
          const items = byStage.get(col.key) ?? [];
          return (
            <div key={col.key} className="space-y-2">
              <div className="flex items-baseline justify-between px-2">
                <span className="text-xs font-semibold uppercase tracking-wide text-text-subtle">
                  {col.label}
                </span>
                <span className="text-xs text-text-muted tabular-nums">{items.length}</span>
              </div>
              <div className={`rounded-lg p-2 ${col.tint} ring-1 ring-black/5`}>
                <ul className="space-y-2">
                  {items.map((p) => (
                    <ProspectCard
                      key={p.id}
                      prospect={p}
                      onAdvance={(next) =>
                        update.mutate({ id: p.id, patch: { stage: next } as Partial<Prospect> })
                      }
                    />
                  ))}
                  {items.length === 0 ? (
                    <li className="px-2 py-3 text-xs text-text-subtle">—</li>
                  ) : null}
                </ul>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ProspectCard({
  prospect,
  onAdvance,
}: {
  prospect: Prospect;
  onAdvance: (stage: Stage) => void;
}) {
  const qc = useQueryClient();
  const stageOptions: Stage[] = ['not_contacted', 'called', 'demoed', 'signed', 'churned'];
  const [emailOpen, setEmailOpen] = React.useState(false);
  const [notes, setNotes] = React.useState(prospect.notes ?? '');
  const [copiedLink, setCopiedLink] = React.useState(false);

  // Persist notes on blur (debounce-free; PATCH is cheap).
  const saveNotes = useMutation({
    mutationFn: async (next: string) => {
      const res = await fetch(
        `${env.apiBaseUrl}/api/operator/sales/prospects/${prospect.id}`,
        {
          method: 'PATCH',
          credentials: 'include',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ notes: next }),
        },
      );
      if (!res.ok) throw new Error(`notes_${res.status}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['operator', 'sales'] }),
  });

  // Mint + copy a fresh claim-link to clipboard so the rep can paste
  // it anywhere (Loom comment, SMS, ad-hoc email). 14-day expiry.
  const copyClaimLink = useMutation({
    mutationFn: async () => {
      if (!prospect.tenant_id) throw new Error('no_tenant');
      const res = await fetch(
        `${env.apiBaseUrl}/api/tenants/${prospect.tenant_id}/claim-link`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'content-type': 'application/json' },
          body: '{}',
        },
      );
      if (!res.ok) throw new Error(`claim_${res.status}`);
      const j = (await res.json()) as { claim_url: string };
      await navigator.clipboard.writeText(j.claim_url);
      return j.claim_url;
    },
    onSuccess: () => {
      setCopiedLink(true);
      window.setTimeout(() => setCopiedLink(false), 2000);
    },
  });
  return (
    <li>
      <Card className="bg-bg ring-1 ring-black/5">
        <CardHeader className="p-3">
          <CardTitle className="text-sm font-medium text-text">
            {prospect.clinic_name}
            {prospect.city ? (
              <span className="ml-1.5 text-xs font-normal text-text-subtle">· {prospect.city}</span>
            ) : null}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 p-3 pt-0 text-xs text-text-muted">
          <div className="flex flex-wrap items-center gap-2">
            {prospect.contact_phone ? (
              <a
                href={`tel:${prospect.contact_phone.replace(/\s+/g, '')}`}
                className="inline-flex items-center gap-1 rounded-full bg-surface px-2 py-0.5 hover:text-text"
              >
                <Phone className="size-3" aria-hidden="true" />
                <span className="tabular-nums">{prospect.contact_phone}</span>
              </a>
            ) : null}
            {prospect.preview_url ? (
              <a
                href={prospect.preview_url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 rounded-full bg-surface px-2 py-0.5 hover:text-text"
              >
                Podgląd <ExternalLink className="size-3" aria-hidden="true" />
              </a>
            ) : null}
            {prospect.loom_url ? (
              <a
                href={prospect.loom_url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 rounded-full bg-surface px-2 py-0.5 hover:text-text"
              >
                <Video className="size-3" aria-hidden="true" />
                Loom
              </a>
            ) : null}
            {prospect.contact_email ? (
              <button
                type="button"
                onClick={() => setEmailOpen(true)}
                className="inline-flex items-center gap-1 rounded-full bg-accent/10 px-2 py-0.5 text-accent hover:bg-accent/20"
              >
                <Mail className="size-3" aria-hidden="true" />
                Wyślij mail
              </button>
            ) : null}
            {prospect.tenant_id ? (
              <button
                type="button"
                onClick={() => copyClaimLink.mutate()}
                disabled={copyClaimLink.isPending}
                className="inline-flex items-center gap-1 rounded-full bg-surface px-2 py-0.5 hover:text-text disabled:opacity-60"
                title="Mint + copy a fresh 14-day claim-link to clipboard"
              >
                {copiedLink ? (
                  <>
                    <Check className="size-3 text-success" aria-hidden="true" />
                    Skopiowano
                  </>
                ) : (
                  <>
                    <Link2 className="size-3" aria-hidden="true" />
                    Kopiuj link
                  </>
                )}
              </button>
            ) : null}
          </div>

          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            onBlur={() => {
              if (notes !== (prospect.notes ?? '')) saveNotes.mutate(notes);
            }}
            placeholder="Notatki z rozmowy…"
            rows={2}
            className="w-full resize-none rounded-md border border-border bg-bg p-2 text-xs text-text outline-none placeholder:text-text-subtle focus:border-accent"
          />
          {saveNotes.isPending ? (
            <p className="text-[10px] text-text-subtle">Zapisuję…</p>
          ) : null}

          <div className="flex flex-wrap items-center gap-1.5">
            {stageOptions
              .filter((s) => s !== prospect.stage)
              .map((s) => (
                <Button
                  key={s}
                  size="sm"
                  variant="ghost"
                  className="h-7 rounded-full bg-surface px-2.5 text-[11px] text-text-muted hover:text-text"
                  onClick={() => onAdvance(s)}
                >
                  → {LABEL[s]}
                </Button>
              ))}
          </div>
        </CardContent>
      </Card>

      {emailOpen ? (
        <EmailComposeDialog
          prospect={prospect}
          open={emailOpen}
          onOpenChange={setEmailOpen}
        />
      ) : null}
    </li>
  );
}

interface SalesTemplate {
  id: string;
  label_pl: string;
  description_pl: string;
  next_stage?: 'called' | 'demoed' | 'churned';
}

interface PreviewResponse {
  ok: boolean;
  to: string | null;
  template_id: string;
  rendered: { subject: string; body_text: string; body_html: string };
  founder_seats_left: number | null;
}

function EmailComposeDialog({
  prospect,
  open,
  onOpenChange,
}: {
  prospect: Prospect;
  open: boolean;
  onOpenChange: (b: boolean) => void;
}) {
  const qc = useQueryClient();
  const [templateId, setTemplateId] = React.useState<string>('cold_outreach_directory');

  const templates = useQuery({
    queryKey: ['sales', 'templates'],
    queryFn: async (): Promise<SalesTemplate[]> => {
      const res = await fetch(`${env.apiBaseUrl}/api/operator/sales/templates`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error(`templates_${res.status}`);
      const j = (await res.json()) as { templates: SalesTemplate[] };
      return j.templates;
    },
  });

  const preview = useQuery({
    queryKey: ['sales', 'preview-email', prospect.id, templateId],
    queryFn: async (): Promise<PreviewResponse> => {
      const res = await fetch(
        `${env.apiBaseUrl}/api/operator/sales/prospects/${prospect.id}/preview-email?template_id=${templateId}`,
        { credentials: 'include' },
      );
      if (!res.ok) throw new Error(`preview_${res.status}`);
      return (await res.json()) as PreviewResponse;
    },
    enabled: open,
  });

  const send = useMutation({
    mutationFn: async () => {
      const t = templates.data?.find((x) => x.id === templateId);
      const res = await fetch(
        `${env.apiBaseUrl}/api/operator/sales/prospects/${prospect.id}/send-email`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({
            template_id: templateId,
            next_stage: t?.next_stage,
          }),
        },
      );
      if (!res.ok) {
        const j = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(j.error ?? 'send_failed');
      }
      return (await res.json()) as { ok: boolean; message_id: string; to: string };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['operator', 'sales'] });
      onOpenChange(false);
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{prospect.clinic_name}</DialogTitle>
          <DialogDescription>
            Wysyłka do: <span className="font-mono">{prospect.contact_email}</span>
            {preview.data?.founder_seats_left != null
              ? ` · Founder-50: ${preview.data.founder_seats_left} miejsc wolnych`
              : null}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium text-text-muted">Szablon</label>
            <select
              value={templateId}
              onChange={(e) => setTemplateId(e.target.value)}
              className="mt-1 block w-full rounded-md border border-border bg-bg px-3 py-2 text-sm"
            >
              {(templates.data ?? []).map((t) => (
                <option key={t.id} value={t.id}>
                  {t.label_pl}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-text-subtle">
              {templates.data?.find((t) => t.id === templateId)?.description_pl}
            </p>
          </div>

          <div>
            <label className="text-xs font-medium text-text-muted">Temat</label>
            <p className="mt-1 rounded-md bg-surface px-3 py-2 text-sm text-text">
              {preview.data?.rendered.subject ?? '…'}
            </p>
          </div>

          <div>
            <label className="text-xs font-medium text-text-muted">Treść</label>
            <pre className="mt-1 max-h-96 overflow-y-auto rounded-md bg-surface p-3 text-xs leading-relaxed text-text whitespace-pre-wrap">
              {preview.data?.rendered.body_text ?? '…'}
            </pre>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Anuluj
          </Button>
          <Button
            onClick={() => send.mutate()}
            disabled={send.isPending || !preview.data || !prospect.contact_email}
          >
            <Send className="size-4" aria-hidden="true" />
            {send.isPending ? 'Wysyłam…' : 'Wyślij teraz'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const LABEL: Record<Stage, string> = {
  not_contacted: 'Nie skont.',
  called: 'Telefon',
  demoed: 'Demo',
  signed: 'Podpisany',
  churned: 'Odszedł',
  dropped: 'Drop',
};
